import { isPlatformBrowser, DatePipe } from '@angular/common';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  PLATFORM_ID,
  TransferState,
  importProvidersFrom,
  inject,
  makeStateKey,
  provideAppInitializer,
} from '@angular/core';
import { WhiteLabelHeaderLoader } from './whitelabel/white-label-header-loader';
import { shouldLog } from './utils/logging';
import { provideFirebaseApp } from '@angular/fire/app';
import { provideAuth } from '@angular/fire/auth';
import { provideStorage } from '@angular/fire/storage';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { getFirestore } from '@angular/fire/firestore';
import { initializeApp } from '@angular/fire/app';
import { getStorage } from '@angular/fire/storage';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { BACKEND, SID } from './interfaces';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
} from '@angular/fire/auth';
import { getApp } from '@angular/fire/app';
import { provideFirestore } from '@angular/fire/firestore'
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import Lara from '@primeuix/themes/lara';
import {
  ApiService,
  AuctionService,
  CookieService,
  ErrorService,
  FirebaseService,
  FirestorePaginationService,
  LoadingService,
  LotViewTransitionService,
  NotificationService,
  PathService,
  ScheduleService,
  SearchService,
  SettingsService,
  StorageService,
  UserService,
} from './services';
import { AlgoliaPaginationService } from './services/algolia-pagination.service';
import { PageService } from './services/page.service';
import { RasmusGuard } from './services/rasmus-guard';
// import { Measurement } from './enums';
import { TrackEventService } from './services/track-event.service';

if (shouldLog()) {
  console.log(`[app.config] environment.backend: ${environment.backend}`);
}

const SID_STATE_KEY = makeStateKey<string>('Site');

const resolveSid = (transferState: TransferState, platformId: object): string => {
  if (isPlatformBrowser(platformId)) {
    const sidFromState = transferState.get<string | null>(SID_STATE_KEY, null);
    if (sidFromState) {
      return sidFromState;
    }
    throw new Error('SID value not found in TransferState during browser bootstrap.');
  }

  const sidFromEnv = (globalThis as { process?: { env?: Record<string, string> } }).process?.env?.sid;
  if (sidFromEnv) {
    return sidFromEnv;
  }

  throw new Error('SID environment variable is not defined on the server.');
};

// const sid = inject(SID) as string;

// const getMeasurementID = () => {
//   switch (environment.sid) {
//       case 'rasmus_auctions_appspot_com':
//         return Measurement.RASMUS;
//        case 'cal_auctions_appspot_com':
//         return Measurement.CAL_AUCTIONS;
//     default:
//       return '';
//   }
// }

export const appConfig: ApplicationConfig = {
  providers: [
    // SID resolves from TransferState in the browser and falls back to the process environment on the server.
    { provide: SID, useFactory: resolveSid, deps: [TransferState, PLATFORM_ID] },
    { provide: BACKEND, useValue: environment.backend },
    // Resolve the active brand's whitelabel header chunk before first render so
    // SSR and hydration agree, while other brands' headers stay code-split.
    provideAppInitializer(() => inject(WhiteLabelHeaderLoader).init()),
    // NOTE: do NOT add withIncrementalHydration() unless @defer `hydrate` triggers are
    // actually in use. With it enabled, Angular keeps the jsaction event contract
    // installed forever (no cleanUp() after event replay), and the contract
    // preventDefault()s clicks on server-rendered <a> elements that have event
    // bindings — auction-card clicks silently stopped navigating (no console/network
    // trace), most visibly on Safari with a cold cache.
    provideClientHydration(withEventReplay()),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withViewTransitions({
        skipInitialTransition: true,
        onViewTransitionCreated: (info) =>
          inject(LotViewTransitionService).onRouterViewTransitionCreated(info),
      }),
    ),
    provideFirebaseApp(() =>
      initializeApp({
        projectId: 'dark-shade',
        appId: '1:1091133704295:web:534b917390645270d46cc2',
        databaseURL: 'https://dark-shade.firebaseio.com',
        storageBucket: 'auction-engine-upload',
        apiKey: 'AIzaSyDU5Q5Qy9xV7FL5oUB3E0d_C4OWoPZaYYU',
        authDomain: 'dark-shade.firebaseapp.com',
        messagingSenderId: '1091133704295',
      }),
    ),
    provideStorage(() => getStorage()),
    // localStorage-FIRST persistence: getAuth()'s default puts IndexedDB first,
    // and WebKit can stall indefinitely inside indexedDB.open() (ITP, private
    // mode, first-open races), hanging auth resolution — the "skeletons forever
    // on Safari" bug. New sessions write to localStorage (synchronous, no stall
    // surface); IndexedDB and sessionStorage stay in the hierarchy so every
    // pre-existing session still rehydrates. No popupRedirectResolver: sign-in
    // uses GIS tokens + signInWithCredential, never signInWithPopup/Redirect,
    // so the resolver would only add dead bundle weight. The persistence
    // classes ride inside an options object, out of reach of AngularFire's
    // zoneWrap argument wrapping (the "cls is not a constructor" bug that
    // login.component dodges for setPersistence).
    provideAuth(() =>
      isPlatformBrowser(inject(PLATFORM_ID))
        ? initializeAuth(getApp(), {
            persistence: [browserLocalPersistence, indexedDBLocalPersistence, browserSessionPersistence],
          })
        : getAuth(),
    ),
    provideFirestore(() => getFirestore()),
    // NOTE: Firebase Functions and Analytics are intentionally NOT provided here.
    // Nothing injects them via DI — callers use getFunctions()/httpsCallable directly,
    // and Firebase Analytics was never read. Keeping them out of the root providers
    // keeps their SDK code out of the initial bundle.
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          prefix: 'p',
          darkModeSelector: false,
          cssLayer: false,
        },
      },
    }),
    /** Imports needed for services. */
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    // Animations load lazily — the full animation engine used to ship in the
    // initial bundle via BrowserAnimationsModule.
    provideAnimationsAsync(),
    // Only the modules whose SERVICES are injected app-wide (MatDialog, MatSnackBar)
    // belong here. Standalone components import their own UI modules; providing
    // Forms/MatFormField/MatInput/etc. at the root did nothing except pull them
    // into the initial bundle.
    importProvidersFrom(MatDialogModule, MatSnackBarModule),
    /** Services providers. */
    { provide: PathService, useClass: PathService },
    { provide: PageService, useClass: PageService },
    { provide: LoadingService, useClass: LoadingService },
    { provide: AlgoliaPaginationService, useClass: AlgoliaPaginationService },
    { provide: ApiService, useClass: ApiService },
    { provide: AuctionService, useClass: AuctionService },
    { provide: RasmusGuard, useClass: RasmusGuard },
    { provide: CookieService, useClass: CookieService },
    { provide: ErrorService, useClass: ErrorService },
    { provide: FirebaseService, useClass: FirebaseService },
    { provide: FirestorePaginationService, useClass: FirestorePaginationService },
    { provide: LoadingService, useClass: LoadingService },
    { provide: NotificationService, useClass: NotificationService },
    { provide: ScheduleService, useClass: ScheduleService },
    { provide: SearchService, useClass: SearchService },
    { provide: SettingsService, useClass: SettingsService },
    { provide: StorageService, useClass: StorageService },
    { provide: UserService, useClass: UserService },
    { provide: TrackEventService, useClass: TrackEventService },
    MessageService,
    DatePipe,
  ],
};
